"use client";

import type { ReactNode } from "react";
import { NumberProfile } from "./NameNumerology.data";

interface NameResultProps {
  name: string;
  result: {
    nameNumber: number;
  };
  profile: NumberProfile;
  resultOffer: ReactNode;
  onReset: () => void;
}

export function NameResult({ name, result, profile, resultOffer, onReset }: NameResultProps) {
  return (
    <div className="space-y-6">
      {/* Name Number Card */}
      <div className="card-sacred p-6 sm:p-8 text-center">
        <p className="text-body-small font-semibold mb-1 text-ish-faint">
          Name Number for &quot;{name}&quot;
        </p>
        <div className="relative inline-flex items-center justify-center w-28 h-28 my-4">
          <div className="w-28 h-28 rounded-full flex items-center justify-center bg-ish-accent/10 border-3 border-ish-accent">
            <span className="text-[40px] font-bold text-intent-brand">{result.nameNumber}</span>
          </div>
        </div>
        <p className="text-[20px] font-bold text-ish-dark">{profile.title}</p>
        <p className="text-body mt-1 text-ish-muted">
          Ruled by {profile.planet} ({profile.planet_hi})
        </p>
      </div>

      {resultOffer}

      {/* Characteristics */}
      <div className="card-sacred p-6 sm:p-8">
        <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">Personality Traits</h2>
        <ul className="space-y-2">
          {profile.characteristics.map((c, i) => (
            <li key={i} className="flex items-start gap-2 text-body text-ish-muted">
              <span className="text-intent-brand">&#9679;</span>
              {c}
            </li>
          ))}
        </ul>
      </div>

      {/* Strengths & Weaknesses */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card-sacred p-5">
          <h3 className="text-body font-bold mb-3 text-success">Strengths</h3>
          <div className="flex flex-wrap gap-2">
            {profile.strengths.map((s) => (
              <span
                key={s}
                className="text-caption font-semibold px-2.5 py-1 rounded bg-success/8 text-success"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
        <div className="card-sacred p-5">
          <h3 className="text-body font-bold mb-3 text-warning">Challenges</h3>
          <div className="flex flex-wrap gap-2">
            {profile.weaknesses.map((w) => (
              <span
                key={w}
                className="text-caption font-semibold px-2.5 py-1 rounded bg-warning/8 text-warning"
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Compatibility & Details */}
      <div className="card-sacred p-6 sm:p-8">
        <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">
          Planet + Career Details
        </h2>
        <div className="grid grid-cols-2 gap-4 text-body">
          <div>
            <p className="text-caption font-semibold mb-1 text-ish-faint">Compatible Numbers</p>
            <p className="font-bold text-success">{profile.compatible.join(", ")}</p>
          </div>
          <div>
            <p className="text-caption font-semibold mb-1 text-ish-faint">Less Compatible</p>
            <p className="font-bold text-warning">{profile.incompatible.join(", ")}</p>
          </div>
          <div>
            <p className="text-caption font-semibold mb-1 text-ish-faint">Lucky Color</p>
            <p className="font-semibold text-ish-dark">{profile.lucky_color}</p>
          </div>
          <div>
            <p className="text-caption font-semibold mb-1 text-ish-faint">Lucky Day</p>
            <p className="font-semibold text-ish-dark">{profile.lucky_day}</p>
          </div>
        </div>
        <div className="divider-warm my-5" />
        <p className="text-caption font-semibold mb-2 text-ish-faint">Suitable Career Fields</p>
        <div className="flex flex-wrap gap-2">
          {profile.career.map((c) => (
            <span
              key={c}
              className="text-caption font-semibold px-2.5 py-1 rounded bg-ish-accent/10 text-intent-brand"
            >
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-body transition-all duration-200 text-ish-muted border border-ish-border"
        >
          Try Another Name
        </button>
      </div>
    </div>
  );
}
